// Sélecteur d'échéance (WKF-013) : date + HEURE + raccourcis. Les raccourcis sont
// EXACTEMENT ceux du menu de report (source unique `snoozePresets`) → cohérence
// totale entre la modale de tâche et les menus « Reporter » (retour PO 2026-07-08).
// L'heure reste TOUJOURS visible. `hidePresets` = juste date + heure (report précis).

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { TimePicker } from '@/components/ui/time-picker'

import { snoozePresets } from './snoozePresets'

interface DueDatePickerProps {
    value: string | null // ISO avec heure
    onChange: (iso: string | null) => void
    /** Autorise l'option « Sans date » (défaut true). Le snooze la désactive
     *  (repousser exige toujours une date/heure). */
    allowNoDate?: boolean
    /** Masque les raccourcis (report précis : le menu propose déjà les presets). */
    hidePresets?: boolean
}

function withTime(base: Date, h: number, m = 0): Date {
    const d = new Date(base)
    d.setHours(h, m, 0, 0)
    return d
}

export function DueDatePicker({ value, onChange, allowNoDate = true, hidePresets = false }: DueDatePickerProps) {
    const { t } = useTranslation()
    const date = value ? new Date(value) : null
    // Mêmes raccourcis (icône + libellé) que le menu de report — source unique.
    const quickOptions = useMemo(() => snoozePresets(new Date()), [])

    const timeValue = date
        ? `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
        : '09:00'

    const setDatePart = (d: Date | undefined) => {
        if (!d) {
            onChange(null)
            return
        }
        // Conserve l'heure déjà choisie (ou 09:00 par défaut).
        const [h, m] = timeValue.split(':').map(Number)
        onChange(withTime(d, h, m).toISOString())
    }

    const setTimePart = (slot: string) => {
        const [h, m] = slot.split(':').map(Number)
        const base = date ?? new Date() // si aucune date, applique à aujourd'hui
        onChange(withTime(base, h, m).toISOString())
    }

    return (
        <div className="space-y-2">
            {!hidePresets && (
                <div className="flex flex-wrap gap-1.5">
                    {quickOptions.map((p) => {
                        const Icon = p.icon
                        return (
                            <Button
                                key={p.key}
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1.5 text-xs font-normal"
                                onClick={() => onChange(p.iso)}
                            >
                                <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                                {t(p.labelKey)}
                            </Button>
                        )
                    })}
                </div>
            )}
            {/* Date + heure côte à côte : l'heure reste TOUJOURS visible (retour PO). */}
            <div className="grid grid-cols-2 gap-2">
                <DatePicker
                    value={date}
                    onChange={setDatePart}
                    onClear={allowNoDate ? () => onChange(null) : undefined}
                    placeholder={t('notifications.task.pickDate', 'Choisir une date')}
                    className="w-full"
                />
                <TimePicker
                    value={date ? timeValue : null}
                    onChange={(v) => v && setTimePart(v)}
                    minuteStep={5}
                    placeholder={t('common.time', 'Heure')}
                    className="w-full"
                />
            </div>
        </div>
    )
}
